import { Box, Grid, Text, useColorModeValue } from '@chakra-ui/react';
import EpisodeCard from 'features/clinical-history/components/EpisodeCard';
import React from 'react';
import { ClinicalEpisode, EstadoEpisodio, PhysicalEvaluation } from 'types/models';

// ─── Mock helpers ─────────────────────────────────────────────────────────────

function mockEp(overrides: Partial<ClinicalEpisode> = {}): ClinicalEpisode {
  return {
    id:                     'ep-1',
    tarjeteroId:            'tarj-1',
    codigoHc:               'HC-2026-001',
    pacienteId:             'pac-preview',
    profesionalId:          'prof-1',
    estado:                 EstadoEpisodio.ABIERTO,
    motivoConsulta:         'Dolor lumbar de 3 semanas de evolución, intensidad EVA 7/10 en reposo, irradiación a miembro inferior derecho.',
    fechaApertura:          '2026-05-01',
    fechaCierre:            null,
    diagnosticoPrincipal:   null,
    codigoCie10:            null,
    diagnosticoSecundario:  null,
    diagnosticoDiferencial: null,
    notaApertura:           null,
    notaCierre:             null,
    anamnesis:              null,
    signosVitales:          null,
    pruebasSolicitadas:     null,
    appointmentId:          null,
    createdAt:              '2026-05-01T09:00:00Z',
    updatedAt:              '2026-05-01T09:00:00Z',
    ...overrides,
  };
}

function mockEval(overrides: Partial<PhysicalEvaluation> = {}): PhysicalEvaluation {
  return {
    id:               'ev-1',
    episodeId:        'ep-1',
    codigoHc:         'HC-2026-001',
    pacienteId:       'pac-preview',
    profesionalId:    'prof-1',
    numeroEvaluacion: 1,
    fechaEvaluacion:  '2026-05-05',
    tipoEvaluacion:   null,
    evaluationData:   null,
    escalaDolor:      7,
    rangoMovimiento:  { flexionLumbar: 30, extension: 10, lateralDerecha: 20 },
    fuerzaMuscular:   null,
    pruebasEspecificas: { lasegue: { resultado: 'positivo' }, bragard: { resultado: 'negativo' } },
    inspeccion:       'Postura antálgica con lateralización izquierda.',
    palpacion:        'Contractura paravertebral bilateral L3-L5.',
    diagnostico:      'Síndrome lumbar agudo con compromiso radicular',
    observaciones:    null,
    createdAt:        '2026-05-05T10:00:00Z',
    updatedAt:        '2026-05-05T10:00:00Z',
    ...overrides,
  };
}

// ─── Mock episodes ────────────────────────────────────────────────────────────

const EP_ABIERTO = mockEp({
  id:           'ep-abierto',
  estado:       EstadoEpisodio.ABIERTO,
  codigoHc:     'HC-2026-012',
  motivoConsulta: 'Gonalgia medial derecha de inicio insidioso tras actividad deportiva intensa. Sin traumatismo directo.',
  notaApertura: 'Paciente deportista amateur, 3 entrenamientos semanales. Refiere inicio progresivo hace 2 semanas.',
});

const EP_EN_TRATAMIENTO = mockEp({
  id:                    'ep-tratamiento',
  estado:                EstadoEpisodio.EN_TRATAMIENTO,
  codigoHc:              'HC-2026-007',
  motivoConsulta:        'Cervicalgia postraumática tras accidente de tráfico. Contractura bilateral C4-C6 con limitación de movilidad.',
  diagnosticoPrincipal:  'Cervicalgia mecánica postraumática con contractura muscular paravertebral',
  codigoCie10:           'M54.2',
  diagnosticoSecundario: 'Síndrome ansioso-depresivo reactivo al accidente',
  anamnesis: {
    motivo:    'Dolor cervical postraumático',
    evolucion: 'Inicio brusco tras colisión vehicular hace 3 semanas. Progresión gradual de la contractura. Mejoría parcial con analgésicos.',
    sintomas:  'Dolor cervical irradiado a hombro derecho EVA 6/10. Cefalea tensional occipital. Mareos ocasionales al girar la cabeza.',
  },
  notaApertura: 'Paciente refiere accidente el 15/04/2026. Colisión por detrás. Usaba cinturón. Sin pérdida de consciencia.',
});

const EP_CERRADO = mockEp({
  id:                   'ep-cerrado',
  estado:               EstadoEpisodio.CERRADO,
  codigoHc:             'HC-2025-089',
  motivoConsulta:       'Tendinitis del manguito rotador hombro derecho. EVA 6/10. Limitación de abducción activa.',
  diagnosticoPrincipal: 'Tendinitis del supraespinoso grado II con bursitis subacromial asociada',
  codigoCie10:          'M75.1',
  fechaApertura:        '2025-08-10',
  fechaCierre:          '2025-10-22',
  notaCierre:           'Alta por mejoría clínica. Paciente recupera rango articular completo. ABD activa 170° sin dolor. Reintegro deportivo progresivo.',
});

const EP_ARCHIVADO = mockEp({
  id:             'ep-archivado',
  estado:         EstadoEpisodio.ARCHIVADO,
  codigoHc:       'HC-2024-033',
  motivoConsulta: 'Gonartrosis bilateral grado II-III. Dolor crónico bilateral con crepitación.',
  fechaApertura:  '2024-03-01',
  fechaCierre:    '2024-07-14',
});

const EVALS = [
  mockEval({ id: 'ev-1', numeroEvaluacion: 1, escalaDolor: 7, diagnostico: 'Síndrome lumbar agudo con compromiso radicular' }),
  mockEval({ id: 'ev-2', numeroEvaluacion: 2, escalaDolor: 4, fechaEvaluacion: '2026-05-15', diagnostico: 'Mejora funcional — continúa contractura' }),
  mockEval({ id: 'ev-3', numeroEvaluacion: 3, escalaDolor: 2, fechaEvaluacion: '2026-05-28', rangoMovimiento: { flexionLumbar: 65, extension: 25 }, diagnostico: 'Alta funcional parcial' }),
  mockEval({ id: 'ev-4', numeroEvaluacion: 4, escalaDolor: 1, fechaEvaluacion: '2026-06-01', diagnostico: 'Resolución casi completa' }),
];

// ─── Section label ────────────────────────────────────────────────────────────

function Section({ label, children }: { label: string; children: React.ReactNode }) {
  const labelColor = useColorModeValue('gray.400', 'gray.500');
  return (
    <Box>
      <Text fontSize='10px' fontWeight='700' color={labelColor} textTransform='uppercase' letterSpacing='wider' mb='12px'>
        {label}
      </Text>
      {children}
    </Box>
  );
}

// ─── Preview page ─────────────────────────────────────────────────────────────

export default function ClinicalEpisodeCardPreview() {
  const bg         = useColorModeValue('gray.50', 'gray.900');
  const titleColor = useColorModeValue('gray.800', 'white');
  const subColor   = useColorModeValue('gray.500', 'gray.400');
  const cardShell  = useColorModeValue('white', 'navy.800');
  const cardBorder = useColorModeValue('gray.200', 'whiteAlpha.100');

  const cb = (name: string) => () => console.log('[preview]', name);

  return (
    <Box minH='100vh' bg={bg} px={{ base: '24px', md: '48px', lg: '80px' }} py='52px'>

      {/* Header */}
      <Box mb='48px'>
        <Text fontSize='2xl' fontWeight='800' color={titleColor} mb='4px'>
          EpisodeCard — Clinical History Feature
        </Text>
        <Text fontSize='sm' color={subColor}>
          features/clinical-history/components/EpisodeCard · todos los estados + variantes
        </Text>
      </Box>

      <Grid templateColumns={{ base: '1fr', xl: '1fr 1fr' }} gap='40px' maxW='1200px'>

        {/* 1 — Abierto sin diagnóstico + canWrite */}
        <Section label='Abierto · sin diagnóstico · canWrite (CTA diagnóstico)'>
          <Box border='1px solid' borderColor={cardBorder} borderRadius='16px' overflow='hidden' bg={cardShell}>
            <EpisodeCard
              episode={EP_ABIERTO}
              onDiagnoseClick={cb('onDiagnoseClick')}
              onEditClick={cb('onEditClick')}
              onStartTreatmentClick={cb('onStartTreatmentClick')}
              onCloseClick={cb('onCloseClick')}
            />
          </Box>
        </Section>

        {/* 2 — En Tratamiento + diagnóstico + anamnesis + acciones */}
        <Section label='En Tratamiento · diagnóstico + anamnesis completa · canWrite'>
          <Box border='1px solid' borderColor={cardBorder} borderRadius='16px' overflow='hidden' bg={cardShell}>
            <EpisodeCard
              episode={EP_EN_TRATAMIENTO}
              onDiagnoseClick={cb('onDiagnoseClick')}
              onEditClick={cb('onEditClick')}
              onCloseClick={cb('onCloseClick')}
            />
          </Box>
        </Section>

        {/* 3 — En Tratamiento + evaluaciones */}
        <Section label='En Tratamiento · con evaluaciones físicas (4 registros)'>
          <Box border='1px solid' borderColor={cardBorder} borderRadius='16px' overflow='hidden' bg={cardShell}>
            <EpisodeCard
              episode={EP_EN_TRATAMIENTO}
              evaluations={EVALS}
              onNewEvaluation={cb('onNewEvaluation')}
              onViewEvaluation={(ev) => console.log('[preview] onViewEvaluation', ev.id)}
              onCloseClick={cb('onCloseClick')}
            />
          </Box>
        </Section>

        {/* 4 — Cerrado + nota cierre · isAdmin */}
        <Section label='Cerrado · nota cierre completa · isAdmin (puede archivar)'>
          <Box border='1px solid' borderColor={cardBorder} borderRadius='16px' overflow='hidden' bg={cardShell}>
            <EpisodeCard
              episode={EP_CERRADO}
              onArchiveClick={cb('onArchiveClick')}
              onReopenClick={cb('onReopenClick')}
            />
          </Box>
        </Section>

        {/* 5 — Archivado · read-only */}
        <Section label='Archivado · read-only (sin acciones)'>
          <Box border='1px solid' borderColor={cardBorder} borderRadius='16px' overflow='hidden' bg={cardShell}>
            <EpisodeCard episode={EP_ARCHIVADO} />
          </Box>
        </Section>

        {/* 6 — Abierto sin diagnóstico · sin permisos de escritura */}
        <Section label='Abierto · sin diagnóstico · read-only (sin CTA)'>
          <Box border='1px solid' borderColor={cardBorder} borderRadius='16px' overflow='hidden' bg={cardShell}>
            <EpisodeCard episode={EP_ABIERTO} />
          </Box>
        </Section>

      </Grid>
    </Box>
  );
}
